// 里（一枚絵の上に施設の印（名前＋矢印）、右に詳細・一括受取・強化）。見出しの右から米問屋（ui/komedoiya.ts）・万屋（ui/yorozuya.ts）。
// 表示は Profile から meta の関数で計算し、操作はサーバーへ送って /api/me を取り直す。
import { suggestPush } from '../ui/push';
import { sfx } from '../audio';
import { FACILITY_IDS, META_CONFIG, VILLAGE, blueprintCap, smithLbMax, smithLevelCap, smithLevelForLb, facilityMaxLevel, facilityUpgradeCost, villagePending, villageRates, type FacilityId, type Profile } from '@hyakki/core';
import { api } from '../api';
import { register } from '../router';
import { act, state, subscribe } from '../state';
import { esc, fmt, onAct, rewardHtml } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { openModal, toast } from '../ui/overlay';
import { errorText, notReadyHtml } from '../ui/status';
import '../ui/fit';
import { openYorozuya } from '../ui/yorozuya';
import { openKomedoiya } from '../ui/komedoiya';
import { attachWalkers, detachWalkers } from '../ui/village-walkers';

let selFac: FacilityId = 'kura';

/** 施設の印の位置（絵に対する%、矢印の先＝建物の上）：l＝横長の絵（4:3）／p＝縦長の絵（3:4） */
const TAG_POS: Record<FacilityId, { l: [number, number]; p: [number, number]; icon: string }> = {
  kura: { l: [32, 9], p: [29, 9], icon: 'coin' },
  hokora: { l: [47, 34], p: [48, 30], icon: 'village' },
  seizaisho: { l: [17, 30], p: [18, 35], icon: 'wood' },
  hyorogura: { l: [85, 39], p: [83, 34], icon: 'potion' },
  dojo: { l: [75, 61], p: [78, 59], icon: 'sortie' },
  kajiya: { l: [25, 56], p: [23, 57], icon: 'enhance' },
  yashiro: { l: [75, 14], p: [75, 12], icon: 'mon' },
} as Record<FacilityId, { l: [number, number]; p: [number, number]; icon: string }>;
/** 時間で貯まる施設 → 資源 */
const STORE: Partial<Record<FacilityId, 'zeni' | 'reidama' | 'zaimoku' | 'exp'>> = { kura: 'zeni', hokora: 'reidama', seizaisho: 'zaimoku', dojo: 'exp' } as Partial<Record<FacilityId, 'zeni' | 'reidama' | 'zaimoku' | 'exp'>>;
const RES_NAME = { zeni: '銭', reidama: '霊玉', zaimoku: '材木', exp: '主EXP' } as const;
/** 施設Lvが上限のときの文：次に上限が上がる制圧地方の数（本当の上限なら Lv だけ） */
function capText(max: number) {
  const v = META_CONFIG.village;
  if (max >= v.maxLevel) return `上限（Lv.${max}）`;
  return `制圧${Math.ceil((max + 1 - v.levelBase) / v.levelPerProvince)}地方で上限↑`;
}

register('village', {
  title: '里',
  nav: 'village',
  mount(el) {
    el.innerHTML = `<div class="page fill village-page"><section class="panel-paper deco village">
      <header class="screen-head"><h1 class="screen-title">${artIcon('village')}里</h1><div class="end"><button class="vil-shop kome" data-act="komedoiya"><img src="/art/shopgirls/face-komachi.webp" alt="">米問屋</button><button class="vil-shop yorozu" data-act="shop"><img src="/art/shopgirls/face-akane.webp" alt="">万屋</button></div></header>
      <div class="tab-body"></div></section></div>`;
    const body = el.querySelector<HTMLElement>('.tab-body')!;
    const draw = () => {
      const p = state.profile;
      if (!p) return void (body.innerHTML = notReadyHtml('里'));
      drawVillage(body, p);
    };
    draw();
    const timer = setInterval(draw, 60_000);
    // 横長⇔縦長の絵が切り替わったら、住人を見えている絵へ
    const onResize = () => state.profile && attachWalkers(body, state.profile);
    window.addEventListener('resize', onResize);
    const off = subscribe((ch) => (ch.has('profile') || ch.has('resources')) && draw());
    const offAct = onAct(el, {
      shop: () => openYorozuya(),
      komedoiya: () => openKomedoiya(),
      fac: (b) => {
        selFac = b.dataset.id as FacilityId;
        draw();
      },
      collect: async (b) => {
        (b as HTMLButtonElement).disabled = true;
        try {
          const r = await act(api.collectVillage());
          sfx('coin');
          // 閉じたら（アプリで、まだなら）満杯の通知をすすめる
          openModal(rewardHtml(r.gain ?? r), { title: '受け取りました', onClose: () => void suggestPush('village') });
        } catch (e) {
          toast(errorText(e), 'error');
          (b as HTMLButtonElement).disabled = false;
        }
      },
      up: async (b) => {
        (b as HTMLButtonElement).disabled = true;
        try {
          await act(api.upgradeFacility(b.dataset.id!));
          sfx('upgrade');
          toast('強化しました', 'good');
        } catch (e) {
          toast(errorText(e), 'error');
          (b as HTMLButtonElement).disabled = false;
        }
      },
    });
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', onResize);
      detachWalkers();
      off();
      offAct();
    };
  },
});

function drawVillage(body: HTMLElement, p: Profile) {
  const pend = villagePending(p, Date.now());
  const left = META_CONFIG.village.capHours - pend.hours;
  const rates = villageRates(p);
  const max = facilityMaxLevel(p);
  const any = pend.zeni + pend.reidama + pend.zaimoku + pend.exp > 0;
  const id = selFac;
  const lv = p.village.levels[id] ?? 0;
  const cost = facilityUpgradeCost(id, lv) as Record<string, number>;
  const capped = lv >= max;
  const store = STORE[id];
  const tag = (f: FacilityId, mode: 'l' | 'p') => {
    const [x, y] = TAG_POS[f][mode];
    const s = STORE[f];
    const has = s && pend[s] > 0;
    return `<button class="fac-tag ${f === id ? 'on' : ''}" style="left:${x}%;top:${y}%" data-act="fac" data-id="${f}" aria-pressed="${f === id}" aria-label="${esc(VILLAGE[f].name)} Lv.${p.village.levels[f] ?? 0}${has ? '・受け取れます' : ''}"><b>${esc(VILLAGE[f].name)}<small aria-hidden="true">Lv.${p.village.levels[f] ?? 0}</small></b>${has ? '<em class="dot"></em>' : ''}</button>`;
  };
  const pendList = (['zeni', 'reidama', 'zaimoku', 'exp'] as const).filter((k) => pend[k] > 0);
  const costOk = (k: string, v: number) => (state.resources[k as 'zeni'] ?? 0) >= v;
  body.innerHTML = `<div class="vil-split">
      <div class="vil-stage">
        <div class="vil-art land" role="group" aria-label="里の施設">${FACILITY_IDS.map((f) => tag(f, 'l')).join('')}</div>
        <div class="vil-art port" role="group" aria-label="里の施設">${FACILITY_IDS.map((f) => tag(f, 'p')).join('')}</div>
      </div>
      <aside class="vil-detail sheet-card" aria-label="${esc(VILLAGE[id].name)}の詳細" title="${esc(VILLAGE[id].desc)}">
        <header class="vd-head"><span class="fac-icon">${icon(TAG_POS[id].icon)}</span><div class="vd-name"><h2>${esc(VILLAGE[id].name)}</h2><b class="ed-lv">Lv.${lv}<small>／上限${max}</small></b></div></header>
        <p class="vd-desc clamp2">${esc(VILLAGE[id].desc)}</p>
        <div class="mini-kv">
          ${id === 'kajiya' ? `<span>${icon('enhance')}装備の強化上限<b>+${smithLevelCap(p)}</b></span>${smithLbMax(p) < META_CONFIG.equipment.lbMax ? `<span>Lv.${smithLevelForLb(smithLbMax(p))}で<b>+${smithLevelCap(p) + META_CONFIG.equipment.levelCapPerLb}</b></span>` : ''}<span>${icon('blueprint')}図面<b>Lv.${blueprintCap(p)}</b>まで</span>` : ''}
          ${store ? `<span aria-label="貯まっている量"><i class="ri ri-${store}"></i><b>${fmt(pend[store])}</b></span><span aria-label="1時間に貯まる量">+<b>${fmt(rates[store])}</b>/時</span>` : ''}
        </div>
        <div class="vd-cost"><span class="vd-cost-l">${capped ? capText(max) : `強化 Lv.${lv}→${lv + 1}`}</span>
          ${capped ? '' : `<span class="mini-kv">${Object.entries(cost)
            .filter(([k]) => k in RES_NAME)
            .map(([k, v]) => `<span aria-label="${esc(RES_NAME[k as keyof typeof RES_NAME] ?? k)}"><i class="ri ri-${k}"></i><b class="${costOk(k, v) ? '' : 'bad'}">${fmt(v)}</b></span>`)
            .join('')}</span>`}</div>
        <p class="vd-full ${left <= 0 ? 'bad' : ''}">${icon('clock')}${left <= 0 ? '満杯です。受け取ると、また貯まり始めます' : `満杯まで あと${left >= 1 ? `${Math.floor(left)}時間` : `${Math.max(1, Math.ceil(left * 60))}分`}`}</p>
        <div class="vil-actions">
          <button class="btn big vd-collect" data-act="collect" ${any ? '' : 'disabled'}><span>一括受取</span>${any ? `<small class="vd-pend">${pendList.map((k) => `<span><i class="ri ri-${k}"></i>${fmt(pend[k])}</span>`).join('')}</small>` : ''}</button>
          <button class="btn primary big" data-act="up" data-id="${id}" ${capped || !Object.entries(cost).every(([k, v]) => costOk(k, v)) ? 'disabled' : ''}>${icon('enhance')}強化</button>
        </div>
      </aside>
    </div>`;
  attachWalkers(body, p);
}
